import React, { useState, useEffect } from "react";
import { Linkedin, Globe, Mail } from "lucide-react";
import { AppConfig } from "../../utils/Constants";
import { useTheme } from "../../context/ThemeContext";

const MaintenanceMode: React.FC<{ message?: string }> = ({ message }) => {
  const { theme } = useTheme();
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  useEffect(() => {
    // Set launch date to February 1st, 2026 at midnight UTC
    const launchDate = new Date("2026-02-01T00:00:00Z").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = launchDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        const milliseconds = difference % 1000;

        setTimeLeft({ days, hours, minutes, seconds, milliseconds });
      } else {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          milliseconds: 0,
        });
      }
    };

    updateCountdown();
    // Update every 10ms for smooth millisecond updates
    const interval = setInterval(updateCountdown, 10);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div className="absolute top-6 left-6 z-[9999] flex items-center">
        <div className="relative flex items-center">
          <div
            className={`relative flex items-center justify-center rounded-lg p-2 ${
              theme === "light"
                ? "bg-background-panel border border-divider/50"
                : "bg-background-paperDark/50 border border-divider-dark/30"
            }`}
          >
            <img
              src={
                theme === "light"
                  ? "https://cdn.forgeworks.ai/assets/forgeworks-logo-black.png"
                  : AppConfig.LOGO_URL
              }
              alt={`${AppConfig.PRODUCT_NAME_SHORT} Logo`}
              className="w-6 h-6 object-contain"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark font-sans px-6 relative overflow-hidden subtle-grid maintenance-grid-subtle">
        <div className="text-center space-y-6 max-w-lg mx-auto relative z-[60] isolate">
          <h1 className="text-3xl md:text-4xl font-semibold text-black dark:text-text-primaryDark leading-tight tracking-tight">
            Preparing Forgeworks for lift off
          </h1>

          <p className="text-base md:text-lg text-black dark:text-text-secondaryDark">
            {message ||
              "We're putting the final touches on our agents and getting everything ready for launch."}
          </p>

          {/* Countdown Timer */}
          <div className="flex flex-col items-center gap-4 mx-auto">
            {/* Days - Huge and centered */}
            <div className="text-center">
              <div className="text-7xl md:text-8xl font-bold text-black dark:text-text-primaryDark leading-none">
                {timeLeft.days}
              </div>
              <div className="text-lg text-black/60 dark:text-text-secondaryDark mt-2">
                {timeLeft.days === 1 ? "day" : "days"}
              </div>
            </div>

            {/* Hours, Minutes, Seconds, Milliseconds - Row below */}
            <div className="flex items-center justify-center gap-2">
              <div className="text-center px-4 py-3 rounded-lg bg-background-paper dark:bg-background-paperDark border border-black/12 dark:border-divider-dark min-w-[70px]">
                <div className="text-2xl font-semibold text-black dark:text-text-primaryDark">
                  {timeLeft.hours}
                </div>
                <div className="text-xs text-black/60 dark:text-text-secondaryDark mt-1">
                  {timeLeft.hours === 1 ? "hour" : "hours"}
                </div>
              </div>
              <span className="text-xl font-medium text-black/40 dark:text-text-secondaryDark/40">
                :
              </span>
              <div className="text-center px-4 py-3 rounded-lg bg-background-paper dark:bg-background-paperDark border border-black/12 dark:border-divider-dark min-w-[70px]">
                <div className="text-2xl font-semibold text-black dark:text-text-primaryDark">
                  {timeLeft.minutes}
                </div>
                <div className="text-xs text-black/60 dark:text-text-secondaryDark mt-1">
                  {timeLeft.minutes === 1 ? "minute" : "minutes"}
                </div>
              </div>
              <span className="text-xl font-medium text-black/40 dark:text-text-secondaryDark/40">
                :
              </span>
              <div className="text-center px-4 py-3 rounded-lg bg-background-paper dark:bg-background-paperDark border border-black/12 dark:border-divider-dark min-w-[70px]">
                <div className="text-2xl font-semibold text-black dark:text-text-primaryDark">
                  {timeLeft.seconds}
                </div>
                <div className="text-xs text-black/60 dark:text-text-secondaryDark mt-1">
                  {timeLeft.seconds === 1 ? "second" : "seconds"}
                </div>
              </div>
              <span className="text-xl font-medium text-black/40 dark:text-text-secondaryDark/40">
                :
              </span>
              <div className="text-center px-4 py-3 rounded-lg bg-background-paper dark:bg-background-paperDark border border-black/12 dark:border-divider-dark min-w-[80px]">
                <div className="text-2xl font-semibold text-black dark:text-text-primaryDark font-mono">
                  {timeLeft.milliseconds.toString().padStart(3, "0")}
                </div>
                <div className="text-xs text-black/60 dark:text-text-secondaryDark mt-1">
                  ms
                </div>
              </div>
            </div>
          </div>

          {/* Action Links */}
          <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
            <a
              href={AppConfig.PRODUCT_SERVICES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-black dark:text-text-secondaryDark hover:underline pointer-events-auto relative z-[70]"
            >
              <Globe className="w-4 h-4" />
              Our Services
            </a>
            <span className="text-black/20 dark:text-text-secondaryDark/20">
              •
            </span>
            <a
              href={AppConfig.LINKEDIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-black dark:text-text-secondaryDark hover:underline pointer-events-auto relative z-[70]"
            >
              <Linkedin className="w-4 h-4" />
              Get Updates
            </a>
            <span className="text-black/20 dark:text-text-secondaryDark/20">
              •
            </span>
            <a
              href={`mailto:${AppConfig.SUPPORT_EMAIL}`}
              className="inline-flex items-center gap-2 text-sm text-black dark:text-text-secondaryDark hover:underline pointer-events-auto relative z-[70]"
            >
              <Mail className="w-4 h-4" />
              Contact Support
            </a>
          </div>

          <footer className="text-xs text-black dark:text-text-secondaryDark mt-8">
            &copy; {new Date().getFullYear()} {AppConfig.PRODUCT_NAME} - All
            rights reserved.
          </footer>
        </div>
      </div>
    </>
  );
};

export default MaintenanceMode;
